import { memo } from 'react';
import PropTypes from 'prop-types';

const ACTIVE_VARIANT_STYLES = {
  purple:
    'border-purple-400/70 bg-purple-100 text-purple-950 dark:border-purple-500 dark:bg-purple-950/50 dark:text-purple-100',
  blue:
    'border-blue-400/70 bg-blue-100 text-blue-950 dark:border-blue-500 dark:bg-blue-950/50 dark:text-blue-100',
  green:
    'border-emerald-400/70 bg-emerald-100 text-emerald-950 dark:border-emerald-500 dark:bg-emerald-950/50 dark:text-emerald-100',
  amber:
    'border-amber-400/70 bg-amber-100 text-amber-950 dark:border-amber-500 dark:bg-amber-950/50 dark:text-amber-100',
  red:
    'border-red-400/70 bg-red-100 text-red-950 dark:border-red-500 dark:bg-red-950/50 dark:text-red-100',
};

const INACTIVE_CHIP_STYLES =
  'border-edge bg-surface text-content-muted hover:bg-surface-hover-subtle dark:bg-gray-900 dark:hover:bg-gray-800';

/**
 * Selectable chip group for drawer filters.
 *
 * @param {object} props
 * @param {Array<{ value: string, label: import('react').ReactNode, disabled?: boolean }>} [props.options=[]]
 * @param {string|string[]} [props.value]
 * @param {'single'|'multiple'} [props.mode='multiple']
 * @param {(nextValue: string|string[], option: { value: string, label: import('react').ReactNode, disabled?: boolean }) => void} [props.onChange]
 * @param {'purple'|'blue'|'green'|'amber'|'red'} [props.activeVariant='purple']
 * @param {boolean} [props.disabled=false]
 * @param {string} [props.emptyText]
 * @param {string} [props.className]
 * @param {string} [props.listClassName]
 * @param {string} [props.chipClassName]
 */
const DrawerFilterChipGroup = ({
  options = [],
  value,
  mode = 'multiple',
  onChange,
  activeVariant = 'purple',
  disabled = false,
  emptyText,
  className = '',
  listClassName = '',
  chipClassName = '',
}) => {
  const isMultiple = mode === 'multiple';
  const selectedValues = Array.isArray(value)
    ? value
    : value
      ? [value]
      : [];
  const activeStyles = ACTIVE_VARIANT_STYLES[activeVariant] ?? ACTIVE_VARIANT_STYLES.purple;

  const isSelected = (optionValue) => selectedValues.includes(optionValue);

  const handleToggle = (option) => {
    if (disabled || option.disabled) return;

    if (isMultiple) {
      const nextValue = isSelected(option.value)
        ? selectedValues.filter((item) => item !== option.value)
        : [...selectedValues, option.value];
      onChange?.(nextValue, option);
      return;
    }

    onChange?.(value === option.value ? '' : option.value, option);
  };

  return (
    <div className={className}>
      {options.length ? (
        <div className={['flex flex-wrap gap-2', listClassName].filter(Boolean).join(' ')}>
          {options.map((option) => {
            const selected = isSelected(option.value);
            const optionDisabled = disabled || option.disabled;

            return (
              <button
                key={option.value}
                type="button"
                className={[
                  'inline-flex h-8 max-w-full items-center rounded-full border px-4 text-xs font-medium leading-none whitespace-nowrap transition-colors',
                  selected ? activeStyles : INACTIVE_CHIP_STYLES,
                  optionDisabled
                    ? 'cursor-not-allowed opacity-50'
                    : 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 focus-visible:ring-offset-2 focus-visible:ring-offset-surface',
                  chipClassName,
                ]
                  .filter(Boolean)
                  .join(' ')}
                aria-pressed={selected}
                disabled={optionDisabled}
                onClick={() => handleToggle(option)}
              >
                <span className="truncate">{option.label}</span>
              </button>
            );
          })}
        </div>
      ) : emptyText ? (
        <p className="text-xs text-content-muted">{emptyText}</p>
      ) : null}
    </div>
  );
};

DrawerFilterChipGroup.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      disabled: PropTypes.bool,
    }),
  ),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  mode: PropTypes.oneOf(['single', 'multiple']),
  onChange: PropTypes.func,
  activeVariant: PropTypes.oneOf(['purple', 'blue', 'green', 'amber', 'red']),
  disabled: PropTypes.bool,
  emptyText: PropTypes.string,
  className: PropTypes.string,
  listClassName: PropTypes.string,
  chipClassName: PropTypes.string,
};

export default memo(DrawerFilterChipGroup);
